"use client";

import React from "react";
import { motion, useAnimationFrame, useMotionTemplate, useMotionValue, useTransform } from "framer-motion";
import { useRef } from "react";
import { cn } from "@/lib/utils";
export function MovingCard({
  borderRadius = "1.75rem",
  children,
  as: Component = "div",
  containerClassName,
  borderClassName,
  duration,
  className,
  isActive,
  borderBackground,
  showGradient,
  ...otherProps
}: {
  borderRadius?: string;
  children: React.ReactNode;
  as?: any;
  containerClassName?: string;
  borderClassName?: string;
  duration?: number;
  className?: string;
  isActive: boolean;
  [key: string]: any;
  borderBackground?: string;
  showGradient?: boolean;
}) {
  return <Component className={cn("relative h-16 w-40  overflow-hidden bg-transparent p-[1px] text-xl ", containerClassName)} style={{
    borderRadius: borderRadius
  }} {...otherProps} data-sentry-element="Component" data-sentry-component="MovingCard" data-sentry-source-file="moving-border.tsx">
      <div className="absolute inset-0" style={{
      borderRadius: `calc(${borderRadius} * 0.96)`
    }}>
        <MovingBorder isActive={isActive} duration={duration} rx="30%" ry="30%" data-sentry-element="MovingBorder" data-sentry-source-file="moving-border.tsx">
          <div className={cn("h-20 w-20 bg-[conic-gradient(from_0deg,#FF69B4,#87CEFA,#FF69B4)]", borderClassName)} style={{
          background: borderBackground
        }} />
        </MovingBorder>
      </div>
      {showGradient && <div className="absolute inset-0" style={{
      background: borderBackground,
      opacity: 0.5
    }} />}

      <div className={cn("relative flex h-full w-full items-center justify-center border border-slate-800 bg-slate-900/[0.8] text-sm text-white antialiased backdrop-blur-xl", className)} style={{
      borderRadius: `calc(${borderRadius} * 0.96)`
    }}>
        {children}
      </div>
    </Component>;
}
export const MovingBorder = ({
  children,
  duration = 2000,
  rx,
  ry,
  isActive,
  ...otherProps
}: {
  children: React.ReactNode;
  duration?: number;
  rx?: string;
  ry?: string;
  isActive: boolean;
  [key: string]: any;
}) => {
  const pathRef = useRef<any>();
  const progress = useMotionValue<number>(0);
  useAnimationFrame(time => {
    const path = pathRef.current;
    if (!path) return;
    try {
      const length = path.getTotalLength();
      if (length && length > 0) {
        const pxPerMillisecond = length / duration;
        progress.set(time * pxPerMillisecond % length);
      }
    } catch (error) {
      console.debug("Path not ready yet");
    }
  });
  const x = useTransform(progress, val => {
    try {
      const point = pathRef.current?.getPointAtLength(val);
      return point?.x ?? 0;
    } catch (error) {
      return 0;
    }
  });
  const y = useTransform(progress, val => {
    try {
      const point = pathRef.current?.getPointAtLength(val);
      return point?.y ?? 0;
    } catch (error) {
      return 0;
    }
  });
  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;
  return <>
      <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="absolute h-full w-full" width="100%" height="100%" {...otherProps} data-sentry-element="svg" data-sentry-source-file="moving-border.tsx">
        <rect fill="none" width="100%" height="100%" rx={rx} ry={ry} ref={pathRef} data-sentry-element="rect" data-sentry-source-file="moving-border.tsx" />
      </svg>
      <motion.div style={{
      position: "absolute",
      top: 0,
      left: 0,
      display: isActive ? "inline-block" : "none",
      transform: isActive ? transform : "translateX(0px) translateY(0px)"
    }} data-sentry-element="unknown" data-sentry-source-file="moving-border.tsx">
        {children}
      </motion.div>
    </>;
};